import { useFilterBySearchText } from "@/hooks/use-filter-by-search-text";
import { useMemo, useState } from "react";
import { GroupedTableColumn, GroupedTableGroup } from "./GroupedTable";
import { isSortableColumn, SortState } from "./lib";

/**
 * Hook to manage column sorting state
 */
export function useColumnSorting<
  TSource extends object,
  TGroupMeta = undefined,
>(
  data: GroupedTableGroup<TSource, TGroupMeta>[],
  columns: GroupedTableColumn<TSource>[],
) {
  const [sortState, setSortState] = useState<SortState<TSource>>({
    column: null,
    direction: null,
  });

  // Get sortable columns with memoization for better performance
  const sortableColumns = useMemo(() => {
    return columns.filter((column) => isSortableColumn(column.key, data));
  }, [columns, data]);

  // Toggle sort for a column (default -> asc -> desc -> default)
  const toggleSort = (columnKey: keyof TSource) => {
    setSortState((prev) => {
      if (prev.column !== columnKey) {
        // New column, start with ascending
        return { column: columnKey, direction: "asc" };
      }

      // Same column, cycle through states
      switch (prev.direction) {
        case null:
          return { column: columnKey, direction: "asc" };
        case "asc":
          return { column: columnKey, direction: "desc" };
        case "desc":
          return { column: null, direction: null };
        default:
          return { column: columnKey, direction: "asc" };
      }
    });
  };

  return {
    sortState,
    sortableColumns,
    toggleSort,
    isSortable: (columnKey: keyof TSource) =>
      sortableColumns.some((col) => col.key === columnKey),
    isActivelySorting: () =>
      sortState.column !== null && sortState.direction !== null,
  };
}

/**
 * Hook to manage search and group filter state (decoupled from sorting)
 */
export function useGroupedTableSearchAndFilters<
  TSource extends object,
  TGroupMeta = undefined,
>(
  data: GroupedTableGroup<TSource, TGroupMeta>[],
  columns: GroupedTableColumn<TSource>[],
  searchableFields?: (keyof TSource)[],
) {
  // Search state
  const allItems = useMemo(() => data.flatMap((group) => group.items), [data]);

  const {
    searchText,
    setSearchText,
    filteredItems: searchFilteredItems,
  } = useFilterBySearchText(
    allItems,
    searchableFields || columns.map((col) => col.key),
  );

  // Filter state - Set of selected group labels
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());

  // Get all group labels for filtering
  const groupLabels = useMemo(() => {
    return Array.from(new Set(data.map((group) => group.label)));
  }, [data]);

  // Apply search first, then group filters (no sorting here)
  const filteredData = useMemo(() => {
    // Start with search-filtered items
    const searchFiltered =
      searchText.trim() === "" ? allItems : searchFilteredItems;

    // Convert back to grouped format for search-filtered items
    let searchFilteredGroups = data
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => searchFiltered.includes(item)),
      }))
      .filter((group) => group.items.length > 0);

    // Then apply group filters
    if (selectedGroups.size > 0) {
      searchFilteredGroups = searchFilteredGroups.filter((group) =>
        selectedGroups.has(group.label),
      );
    }

    return searchFilteredGroups;
  }, [data, searchFilteredItems, searchText, allItems, selectedGroups]);

  // Group filter management functions
  const updateGroupFilter = (groupLabel: string, selected: boolean) => {
    setSelectedGroups((prev) => {
      const newSelection = new Set(prev);
      if (selected) {
        newSelection.add(groupLabel);
      } else {
        newSelection.delete(groupLabel);
      }
      return newSelection;
    });
  };

  const clearAllFilters = () => {
    setSelectedGroups(new Set());
  };

  const clearGroupFilters = () => {
    setSelectedGroups(new Set());
  };

  return {
    // State
    searchText,
    setSearchText,
    selectedGroups,

    // Data
    filteredData,
    groupLabels,

    // Actions
    updateGroupFilter,
    clearAllFilters,
    clearGroupFilters,

    // Computed
    hasActiveFilters: selectedGroups.size > 0 || searchText.trim() !== "",
    activeFilterCount: selectedGroups.size,
  };
}
